import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND, FARM_FOOTPRINTING_DAIRY_MOORLAND } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin, Typography } from "components";

interface MoorlandProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const MoorlandForm = ({ primaryDataId, readOnly, farmId, year }: MoorlandProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [moorlandActive, setMoorlandActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND | null>(null);
  const [fodderProductionAreaHa, setFodderProductionAreaHa] = useState<number | null>(null);
  const [annualWaterLevelCm, setannualWaterLevelCm] = useState<number | null>(null);

  const { data: dairyPrimaryDataQuery, isLoading, isSuccess } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      moorlandActive: moorlandActive,
      fodderProductionAreaHa: fodderProductionAreaHa,
      annualWaterLevelCm: annualWaterLevelCm,
      isSubmitted: false,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData({});
      }
    },
    [isLoading, mutateDairyPrimaryData, readOnly]
  );

  useEffect(() => {
    if (dairyPrimaryDataQuery && isSuccess) {
      setMoorlandActive(dairyPrimaryDataQuery.moorlandActive ?? null);
      setFodderProductionAreaHa(dairyPrimaryDataQuery.fodderProductionAreaHa ?? null);
      setannualWaterLevelCm(dairyPrimaryDataQuery.annualWaterLevelCm ?? null);

      formRef.current?.setFieldsValue({
        fodderProductionAreaHa: dairyPrimaryDataQuery.fodderProductionAreaHa ?? null,
        annualWaterLevelCm: dairyPrimaryDataQuery.annualWaterLevelCm ?? null,
      });

      const fieldsToValidate = (["fodderProductionAreaHa", "annualWaterLevelCm"] as const).some(
        (field) => dairyPrimaryDataQuery[field] !== null && dairyPrimaryDataQuery[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-dairy-moorland-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("dairyMoorlandForm.question.moorlandRadioQuestion")}
              {true && <span className="required-asterisk">*</span>}
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-dairy-moorland-radio"
              onChange={({ target }) => {
                setMoorlandActive(target.value);
              }}
              value={moorlandActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_DAIRY_MOORLAND.YES, FARM_FOOTPRINTING_DAIRY_MOORLAND.NO].map((moorland) => ({
                label: t(`dairyMoorlandForm.moorlandOptions.${moorland}`),
                value: moorland,
              }))}
            />
          </Flex>

          {moorlandActive === FARM_FOOTPRINTING_DAIRY_MOORLAND.YES && (
            <>
              <InputNumber
                disabled={readOnly}
                name="fodderProductionAreaHa"
                dataTestId="footprinting-dairy-fodder-production-area-input"
                label={t("dairyMoorlandForm.question.fodderProductionAreaLabel")}
                suffix={t("dairyMoorlandForm.question.fodderProductionAreaSuffix")}
                value={fodderProductionAreaHa}
                initialValue={fodderProductionAreaHa}
                onChange={(v) => setFodderProductionAreaHa(v)}
                validateFirst
                guidanceText={t("dairyMoorlandForm.notSure")}
                rules={[
                  { required: true, message: t("dairyMoorlandForm.error.validNumber") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("dairyMoorlandForm.error.validNumber")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />

              <InputNumber
                disabled={readOnly}
                name="annualWaterLevelCm"
                dataTestId="footprinting-dairy-annual-water-lever-input"
                label={t("dairyMoorlandForm.question.annualWaterLevelLabel")}
                suffix={t("dairyMoorlandForm.question.annualWaterLevelSuffix")}
                value={annualWaterLevelCm}
                initialValue={annualWaterLevelCm}
                onChange={(v) => setannualWaterLevelCm(v)}
                validateFirst
                guidanceText={t("dairyMoorlandForm.notSure")}
                rules={[
                  { required: true, message: t("dairyMoorlandForm.error.validNumber") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("dairyMoorlandForm.error.validNumber")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

MoorlandForm.Key = "dairy.moorland";
export { MoorlandForm };
